import {useTransactionByIdQuery} from '@transactions/graphql';
import * as React from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import TransactionDetail from 'src/DetailComponents/TransactionDetail';
import Empty from 'src/ListView/Empty';
import Loading from 'src/Loading';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import type {TransferType} from '@transactions/graphql';

const getTransactionLabel = (transaction: {
  netAmount: number;
  payee?: string | null;
  transferType?: TransferType | null;
}): string => {
  if (transaction.netAmount !== 0) {
    return transaction.payee || 'Untitled Payment';
  }
  // A transfer's payee (its counterparty) names it better than its kind
  // alone: "Amazon" beats "Refund" in the breadcrumb.
  if (transaction.payee) {
    return transaction.payee;
  }
  if (transaction.transferType && transaction.transferType in TRANSFER_TYPE_LABELS) {
    return TRANSFER_TYPE_LABELS[transaction.transferType];
  }
  return 'Transfer';
};

const TransactionDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transactionId = params.id || '';
  const {data, loading, error, refetch} = useTransactionByIdQuery({
    variables: {id: transactionId},
  });

  // Mutations returning UnifiedTransaction overwrite the Apollo cache without
  // consumptions.rawTransaction. Detect the stale shape and refetch once.
  React.useEffect(() => {
    const consumptions = data?.transactionById?.consumptions;
    if (consumptions?.some((c) => !c.rawTransaction)) {
      void refetch();
    }
  }, [data, refetch]);

  const transaction = data?.transactionById;
  const breadcrumbLabel = transaction
    ? getTransactionLabel(transaction)
    : transactionId;

  useBreadcrumb({
    label: breadcrumbLabel,
    fallback: [{label: 'Ledger', href: '/ledger'}],
  });

  if (!transactionId) {
    return <Empty>No transaction selected</Empty>;
  }

  if (loading && !data) {
    return <Loading size="medium" />;
  }

  if (error && !transaction) {
    return <Empty>Failed to load transaction: {error.message}</Empty>;
  }

  if (!transaction) {
    return <Empty>Transaction not found</Empty>;
  }

  const handleReset = () => {
    navigate('/ledger');
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-8">
      <TransactionDetail
        transaction={transaction}
        onReset={handleReset}
        highlightLineId={searchParams.get('line')}
      />
    </div>
  );
};

export {TransactionDetailPage};
